import { useEffect } from 'react'
import { useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'
import { TranscriptDisplay } from './TranscriptDisplay'
import { ProgressSteps } from './ProgressSteps'
import { CollectedInfoDisplay } from './CollectedInfoDisplay'
import { ConsentPanel } from './ConsentPanel'
import { Button } from '../common/Button'
import { useAuth0 } from '../../hooks/useAuth0'

interface CallStatusDisplayProps {
  callId: Id<'calls'>
  onRetry?: () => void
}

/**
 * Container component that orchestrates the call status UI
 * Handles different call states: initiated, ringing, active, completed, failed
 *
 * Layout (Option D - Mobile Optimized):
 * - Progress steps as horizontal dots at top
 * - Collected info as horizontal card grid
 * - Collapsible transcript at bottom
 *
 * Note: Auth0 redirect happens in ConsentPanel when user clicks Save,
 * not here based on call status. This ensures immediate redirect on user action.
 */
export function CallStatusDisplay({ callId, onRetry }: CallStatusDisplayProps) {
  const call = useQuery(api.calls.getCallById, { callId })
  const { loginWithRedirect } = useAuth0()

  // Fallback: if we reach the completed state but the ConsentPanel's redirect
  // didn't fire, check for the callId in localStorage and redirect to Auth0.
  // This covers the race condition where the component re-renders before
  // loginWithRedirect could execute.
  useEffect(() => {
    if (
      call?.status === 'completed' &&
      call.currentStep !== 'consent' &&
      call.currentStep !== 'finalization'
    ) {
      const voiceOnboardingCallId = localStorage.getItem('voiceOnboardingCallId')
      if (voiceOnboardingCallId) {
        // ConsentPanel stored the callId but didn't get to redirect - do it now
        loginWithRedirect({
          authorizationParams: {
            screen_hint: 'signup',
          },
          appState: {
            returnTo: '/dashboard',
          },
        })
      }
    }
  }, [call?.status, call?.currentStep, loginWithRedirect])

  if (!call) {
    return (
      <div style={centerContainerStyles}>
        <div style={spinnerStyles} />
        <div style={statusTextStyles}>Loading call information...</div>
      </div>
    )
  }

  // Initiated state - call is being placed
  if (call.status === 'initiated') {
    return (
      <div style={centerContainerStyles}>
        <div style={spinnerStyles} />
        <div style={statusTextStyles}>Calling your phone...</div>
        <div style={helpTextStyles}>You should receive a call shortly</div>
      </div>
    )
  }

  // Ringing state - phone is ringing
  if (call.status === 'ringing') {
    return (
      <div style={centerContainerStyles}>
        <div style={phoneIconStyles}>📞</div>
        <div style={statusTextStyles}>Phone is ringing...</div>
        <div style={helpTextStyles}>Please answer to continue</div>
      </div>
    )
  }

  // Failed state - call failed
  if (call.status === 'failed') {
    return (
      <div style={centerContainerStyles}>
        <div style={errorIconStyles}>⚠️</div>
        <div style={statusTextStyles}>Call Failed</div>
        <div style={helpTextStyles}>
          We couldn't connect the call. Please check your phone number and try again.
        </div>
        <div style={buttonGroupStyles}>
          {onRetry && (
            <Button onClick={onRetry} style={{ flex: 1 }}>
              Try Again
            </Button>
          )}
          <Button
            onClick={() => (window.location.href = '/onboarding')}
            variant="secondary"
            style={{ flex: 1 }}
          >
            Fill Out Form Instead
          </Button>
        </div>
      </div>
    )
  }

  // Completed state - but only show if we're past the consent/finalization steps
  // If the user is still on consent or finalization, keep showing the active UI
  // so the ConsentPanel can finish its Auth0 redirect flow without being unmounted
  if (
    call.status === 'completed' &&
    call.currentStep !== 'consent' &&
    call.currentStep !== 'finalization'
  ) {
    return (
      <div style={centerContainerStyles}>
        <div style={successIconStyles}>✓</div>
        <div style={statusTextStyles}>Setup Complete!</div>
        <div style={helpTextStyles}>Redirecting you to create your account...</div>
        <div style={spinnerStyles} />
      </div>
    )
  }

  // Active state - call in progress
  // Option D layout: stacked vertical with progress on top, cards, then collapsed transcript
  return (
    <div style={activeContainerStyles}>
      {/* Header with call status */}
      <div style={headerStyles}>
        <div style={activeIndicatorStyles}>
          <div style={pulseDotStyles} />
          <span>Call in Progress</span>
        </div>
        {call.duration !== undefined && (
          <div style={durationStyles}>
            {Math.floor(call.duration / 60)}:{String(call.duration % 60).padStart(2, '0')}
          </div>
        )}
      </div>

      {/* Progress Steps - horizontal dots */}
      <ProgressSteps callId={callId} />

      {/* Show consent panel during consent and finalization steps.
          During finalization, the ConsentPanel stays mounted so its Auth0
          redirect can complete without being interrupted by re-renders. */}
      {call.currentStep === 'consent' || call.currentStep === 'finalization' ? (
        <div style={consentLayoutStyles}>
          {/* Collected Info - stays at top */}
          <CollectedInfoDisplay callId={callId} />

          {/* Consent Panel - stays mounted through finalization for Auth0 redirect */}
          <ConsentPanel
            callId={callId}
            currentStep={call.currentStep}
            lovedOneName={call.collectedData?.lovedOneName}
            lovedOnePhone={call.collectedData?.lovedOnePhone}
            existingPhone={call.collectedData?.notificationPhone}
            existingEmail={call.collectedData?.notificationEmail}
          />

          {/* Transcript - collapsed by default on consent */}
          <TranscriptDisplay callId={callId} defaultCollapsed={true} />
        </div>
      ) : (
        <div style={mainLayoutStyles}>
          {/* Collected Info - card grid */}
          <CollectedInfoDisplay callId={callId} />

          {/* Transcript - full height */}
          <TranscriptDisplay callId={callId} defaultCollapsed={false} />
        </div>
      )}
    </div>
  )
}

// Styles
const centerContainerStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '48px 24px',
  minHeight: '400px',
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
}

const activeContainerStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}

const headerStyles: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 20px',
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
}

const activeIndicatorStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '14px',
  fontWeight: 600,
  color: '#374151',
}

const pulseDotStyles: React.CSSProperties = {
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  backgroundColor: '#10b981',
  boxShadow: '0 0 0 0 rgba(16, 185, 129, 0.7)',
  animation: 'greenPulse 2s ease-in-out infinite',
}

const durationStyles: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: 500,
  color: '#6b7280',
  fontFamily: 'monospace',
}

const mainLayoutStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}

const consentLayoutStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}

const spinnerStyles: React.CSSProperties = {
  width: '48px',
  height: '48px',
  border: '4px solid #e5e7eb',
  borderTop: '4px solid #3b82f6',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  marginBottom: '16px',
}

const phoneIconStyles: React.CSSProperties = {
  fontSize: '64px',
  marginBottom: '16px',
  animation: 'bounce 1s ease-in-out infinite',
}

const errorIconStyles: React.CSSProperties = {
  fontSize: '64px',
  marginBottom: '16px',
}

const successIconStyles: React.CSSProperties = {
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  backgroundColor: '#10b981',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '48px',
  fontWeight: 700,
  marginBottom: '16px',
}

const statusTextStyles: React.CSSProperties = {
  fontSize: '24px',
  fontWeight: 600,
  color: '#1f2937',
  marginBottom: '8px',
  textAlign: 'center',
}

const helpTextStyles: React.CSSProperties = {
  fontSize: '15px',
  color: '#6b7280',
  textAlign: 'center',
  maxWidth: '400px',
}

const buttonGroupStyles: React.CSSProperties = {
  display: 'flex',
  gap: '12px',
  marginTop: '24px',
  width: '100%',
  maxWidth: '500px',
}
